import { iconNames, type IconName } from '@customerio/pluma-icons';
import { z } from '@customerio/hydra-validation';
import type { ObjectType as UIAPIObjectType } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * ObjectType entity — workspace-scoped object-type metadata (segments,
 * journeys, snippets, etc. can target specific object types).
 *
 * Backend at `/v1/environments/:environment_id/object_types`
 * (`listObjectTypes` in `services/ui_api/objects/list_types.go`). No
 * `created`/`updated` on the wire. Reads live in `queries.ts`;
 * create/update/delete live in `mutations.ts`.
 *
 * **`id` is `string`** per the layer convention even though the wire
 * encodes it as `number` — same Tag/SubscriptionTopic/Snippet coercion.
 */

export type ObjectType = Omit<UIAPIObjectType, 'id'> & {
  id: string;
};

const ObjectTypeFromApi = readSchema<ObjectType>(
  z.looseObject({
    id: z.number().transform(String),
  }),
);

/** @public */
export const objectType = defineEntity<ObjectType, ObjectType, void, undefined, 'workspace'>({
  name: 'object_type',
  scope: 'workspace',
  pk: (t) => t.id,
  schema: { read: ObjectTypeFromApi },
});

export const objectTypeKeys = objectType.keys;

export function parseObjectType(raw: UIAPIObjectType): ObjectType {
  return objectType.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions                         */
/* ------------------------------------------------------------------------- */

/**
 * The icon an object type is shown with, falling back to the generic object
 * mark. A type can name an icon Pluma does not have, so the name is checked
 * rather than trusted.
 *
 * This returns `IconName` and so depends on `@customerio/pluma-icons`, which
 * this layer previously avoided so callers could cast instead. Three of them
 * had grown their own copy of the check; one answer that is already the type
 * a caller needs is worth the import, and the casts it replaces were the
 * thing actually hiding a bad icon name.
 *
 * @public
 */
export function objectTypeIcon(type: Pick<ObjectType, 'icon'>): IconName {
  return iconNames.find((name) => name === type.icon) ?? 'object';
}

/** Resolve canonical slug URLs first, with the numeric-id legacy URL as fallback. */
export function getObjectTypeBySlug(types: readonly ObjectType[], slugOrId: string): ObjectType | undefined {
  return types.find(({ slug }) => slug === slugOrId) ?? types.find(({ id }) => id === slugOrId);
}

/** Include newly-written attributes immediately while the object API catches up. */
export function updateObjectTypeSentAttributes(
  type: ObjectType,
  attributes: Readonly<Record<string, string>>,
): ObjectType {
  return { ...type, sent_attributes: [...new Set([...(type.sent_attributes ?? []), ...Object.keys(attributes)])] };
}

/** Include newly-written relationship attributes immediately while the object API catches up. */
export function updateObjectTypeSentRelAttributes(
  type: ObjectType,
  attributes: Readonly<Record<string, string>>,
): ObjectType {
  return {
    ...type,
    sent_rel_attributes: [...new Set([...(type.sent_rel_attributes ?? []), ...Object.keys(attributes)])],
  };
}

/**
 * Object types by name, as Ember's `objectTypes.sortedObjectTypes` orders them: a plain code-point
 * comparison, so "Zebras" sorts before "apples".
 */
export function sortObjectTypesByName<T extends Pick<ObjectType, 'name'>>(types: readonly T[]): T[] {
  return [...types].sort((a, b) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });
}
